import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { PRIVACY_CLASSNAMES } from "@/lib/analytics/shared";
import { getFileHref, fileHasPage } from "@/lib/path-utils";
import { classNames } from "@/lib/utils";
import { useLog } from "@/services/logs.service";
import { HumanEvaluator } from "@/types/app/evaluator";
import { getFileSupportsEvaluators } from "@/types/app/file";
import Button from "@components/library/Button";
import { EvaluatorLog, getFileFromLog, Log } from "@/types/app/log";
import { InlineMenu } from "@components/SimpleTable/InlineMenu";
import { ContextMenu } from "@components/library/ContextMenu";
import Drawer from "@components/library/Drawer";
import { PageSectionHeader } from "@components/library/PageSectionHeader";
import Tabs from "@components/library/Tabs/Tabs";
import { ArrowRightIcon, DotsVerticalIcon } from "@heroicons/react/outline";
import { useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import CodePreview from "../Code/CodePreview";
import VersionDetails from "../Configs/VersionDetails";
import { DrawerTitle } from "../Drawers/DrawerTitle";
import DrawerFeedbackSection from "./Feedback/DrawerFeedbackSection";
import { EvaluationDetails } from "./Feedback/EvaluationDetails";
import FeedbackDetails from "./Feedback/FeedbackDetails";
import { LogContent } from "./LogContent";
import LogDrawerHeader from "./LogsDrawerHeader";
import MetadataDetails from "./MetadataDetails";
import { capitalize } from "lodash";
import { DebugJsonViewer } from "../DebugJsonViewer";
import { UpDownPaginationButtons } from "../PaginationButtons";
import ActionsMenu, { Action } from "../Tables/ActionsColumn/ActionsMenu";
import useDatasetDialog from "../Datasets/useDatasetDialog";
import useConfirm from "@/context/useConfirm";
import { deleteLogs } from "@/services/logs.service";
import PlusIcon from "@heroicons/react/outline/PlusIcon";
import { ToastVariant, hlToast } from "@components/library/Toast";
import TrashIcon from "@heroicons/react/outline/TrashIcon";
import { ButtonWithShortcut } from "@components/atoms/ButtonWithShortcut";
import KeyboardShortcut from "@components/library/KeyboardShortcut";
import { capitalizedFileType } from "@/services/files.service";
import { useRouter } from "next/router";
import { useDrawer } from "@/context/useDrawer";

interface LogDrawerProps {
  log: Log | null;
  open: boolean;
  onClose: () => void;
  width?: number;
  style?: React.CSSProperties;
  contentWrapperStyle?: React.CSSProperties;
  closeable?: boolean;

  /** Callback when feedback is added.
   *
   * Parents should use this to update the display. (It's common for drawers to store the Log itself, and that needs to be mutated.)
   * If either `onAddFeedback` or `onRunEvaluations` is not provided, the "Performance" tab will not be shown.
   */
  onAddFeedback?: (evaluatorLog: EvaluatorLog) => void;
  onRunEvaluations?: (evaluatorLogs: EvaluatorLog[]) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  allowRunEvaluations?: boolean;
  keyboard?: boolean;
  onDelete?: () => void;
  maskClosable?: boolean;
  currentPage?: "logs" | "editor" | "sessions";
  /** Override Human Evaluators
   *
   * This is used to display the specified feedback rows as opposed to the monitoring evaluators
   * attached to the File, allowing users to provide feedback for Human Evaluators that aren't part
   * of the File's monitoring evaluators.
   * E.g. For an Evaluation Report with human Evaluators that aren't part of the File's monitoring evaluators.
   */
  overrideHumanEvaluators?: HumanEvaluator[];

  paginationShortcuts?: {
    prev: string[];
    next: string[];
    prevTooltip: string;
    nextTooltip: string;
  };
}

export const LogDrawer = ({
  log,
  open,
  onClose,
  onAddFeedback,
  onNext,
  onPrevious,
  width = 736,
  style,
  contentWrapperStyle,
  closeable = true,
  allowRunEvaluations = false,
  onRunEvaluations,
  onDelete,
  keyboard = true,
  overrideHumanEvaluators,
  maskClosable = true,
  currentPage = "logs",
  paginationShortcuts,
}: LogDrawerProps) => {
  const router = useRouter();
  const { slug } = useActiveOrganization();
  const datasetContext = useDatasetDialog();
  const { setOpen: setDrawerOpen } = useDrawer();
  const confirm = useConfirm();

  const fileFromLog = log ? getFileFromLog(log) : null;

  // The monitoring File is the active file/"project" set in the URL and this File has the known attached evaluators.
  // This file might not be exactly the same as the file passed in, because we might be rendering this LogDrawer
  // in a different context like a session etc. Therefore, we only show the monitoring file evaluators (the evaluators
  //  that are enabled for this file) if the two files are the same. This means we can add a run button next to them.
  const { file: monitoringFile } = useMonitoringFile();
  const monitoringFileIsLogFile = fileFromLog && monitoringFile?.id === fileFromLog.id;

  const feedbackEvaluators = overrideHumanEvaluators
    ? overrideHumanEvaluators
    : monitoringFileIsLogFile && monitoringFile && "evaluators" in monitoringFile && monitoringFile.evaluators
      ? (
          monitoringFile.evaluators.filter(({ version }) => version?.spec.evaluator_type === "human") as {
            version: HumanEvaluator;
          }[]
        ).map(({ version }) => version)
      : [];

  const logFileSupportsEvaluators = fileFromLog && getFileSupportsEvaluators(fileFromLog);

  const showPerformanceTab = onAddFeedback && onRunEvaluations && logFileSupportsEvaluators;

  // Extract log actions for the ActionsMenu
  const actions: Action<Log | null>[] = [];

  useHotkeys(
    "e",
    (e) => {
      e.preventDefault();
      if (fileFromLog && fileHasPage(fileFromLog, "editor")) {
        router.push(`${getFileHref({ slug, id: fileFromLog.id, page: "editor" })}/${fileFromLog.version_id}`);
      }
    },
    { enabled: open },
    [open, router],
  );

  // Add to Dataset action - both as menu item and standalone button
  if (datasetContext) {
    actions.push({
      menuItemProps: ({ data }) => {
        if (!data) return null;
        return {
          onClick: () => {
            datasetContext.setLogIds([data.id]);
            datasetContext.setOpen(true);
          },
          IconLeft: PlusIcon,
          children: "Add to Dataset",
          withinDropdownMenu: true,
        };
      },
    });
  }

  // Open in Editor action - both as menu item and standalone button
  actions.push({
    menuItemProps: ({ data }) => {
      if (!data) return null;
      const file = getFileFromLog(data);
      if (!file || !fileHasPage(file, "editor")) return null;

      // Disable the Editor button if the user is already on the editor page AND the editor page is for the version that this log belongs to
      // (router.query.config is an array where the first element is the version id)
      const isDisabled = currentPage === "editor" && router.query.config?.[0] === file.version_id;
      return {
        disabled: isDisabled,
        href: `${getFileHref({ slug, id: file.id, page: "editor" })}/${file.version_id}?dataIds=${data.id}`,
        IconLeft: ArrowRightIcon,
        children: "Editor",
        withinDropdownMenu: true,
        onClick: (e) => {
          setDrawerOpen(false);
        },
      };
    },
  });

  // Show session action - only in menu
  actions.push({
    menuItemProps: ({ data }) => {
      if (!data || !data.session_id) return null;
      const file = getFileFromLog(data);
      if (!file) return null;

      return {
        href: `${getFileHref({ slug, id: file.id, page: "sessions" })}/${data.session_id}?datumId=${data.id}`,
        IconLeft: ArrowRightIcon,
        children: "Show session",
        withinDropdownMenu: true,
        onClick: (e) => {
          setDrawerOpen(false);
        },
      };
    },
  });

  // Show in Logs action - only in menu
  actions.push({
    menuItemProps: ({ data }) => {
      if (!data) return null;
      const file = getFileFromLog(data);
      if (!file) return null;

      return {
        href: `${getFileHref({ slug, id: file.id, page: "logs" })}?id=${data.id}`,
        disabled: currentPage === "logs",
        IconLeft: ArrowRightIcon,
        children: "Show in Logs",
        withinDropdownMenu: true,
        onClick: (e) => {
          setDrawerOpen(false);
        },
      };
    },
  });

  // Delete action - only in menu
  if (onDelete) {
    actions.push({
      menuItemProps: ({ data }) => {
        if (!data) return null;

        return {
          disabled: !onDelete,
          onClick: async () => {
            await confirm({
              title: `Are you sure you want to delete this Log?`,
              content: "This action cannot be undone.",
              confirmationText: "Delete",
              cancellationText: "Cancel",
            });
            await deleteLogs([data.id]);
            hlToast({
              title: `Deleted Log`,
              variant: ToastVariant.Info,
            });
            onDelete && onDelete();
          },
          IconLeft: TrashIcon,
          children: "Delete",
          withinDropdownMenu: true,
        };
      },
    });
  }

  return (
    <Drawer
      open={open && log !== null}
      onClose={onClose}
      id={log?.id}
      style={style}
      contentWrapperStyle={contentWrapperStyle}
      width={width}
      keyboard={keyboard}
      idType={fileFromLog?.type === "flow" ? "flow" : undefined}
      closable={closeable}
      extra={
        <div
          className={classNames(
            "flex items-center justify-end gap-12",
            // If closeable, Antd renders an X button and we need to account for that width by adding extra padding on the right.
            // Otherwise, the standard right padding is applied.
            closeable ? "pr-52" : "pr-12",
          )}
        >
          <ActionsMenu
            actions={actions}
            data={log}
            trigger={
              <Button size={24} IconRight={DotsVerticalIcon}>
                Actions
              </Button>
            }
          />
          <div className="h-24 border-l border-stroke-base-3" />
          <UpDownPaginationButtons
            handlePrev={onPrevious}
            handleNext={onNext}
            shortcutPrev={paginationShortcuts?.prev}
            shortcutNext={paginationShortcuts?.next}
            shortcutPrevTooltip={paginationShortcuts?.prevTooltip}
            shortcutNextTooltip={paginationShortcuts?.nextTooltip}
          />
        </div>
      }
      title={
        log && (
          <DrawerTitle
            title={(fileFromLog ? capitalizedFileType(fileFromLog.type) : "") + " Log"}
            entityType={fileFromLog?.type ?? "prompt"}
            entityId={log.id}
            copyText="Log ID"
            linkToCopy={`${window.location.origin}${getFileHref({ slug, id: fileFromLog?.id ?? "", page: "logs" })}?id=${log.id}`}
            copyLinkTooltip="Copy Link to Log"
          />
        )
      }
      maskClosable={maskClosable}
    >
      <>
        {log && fileFromLog && (
          <div className="flex h-full flex-col pt-32">
            <div className="mb-12 flex  items-start justify-between px-24">
              <LogDrawerHeader log={log} />
            </div>

            <Tabs.Group as="div" className="flex h-full grow flex-col px-24">
              <Tabs.List>
                <Tabs.Tab>Details</Tabs.Tab>
                {showPerformanceTab && <Tabs.Tab>Performance</Tabs.Tab>}
                <Tabs.Tab>{capitalize(fileFromLog.type)}</Tabs.Tab>
                <Tabs.Tab>Metadata</Tabs.Tab>
                <Tabs.Tab>JSON</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panels className="">
                <Tabs.Panel className="py-24">
                  {/* Details */}
                  <div className="flex flex-col gap-y-12">
                    <div className="mb-40 flex h-full grow flex-col justify-between">
                      <LogContent log={log} />

                      {logFileSupportsEvaluators && feedbackEvaluators.length > 0 && onAddFeedback && (
                        <div className="my-56 grow">
                          <PageSectionHeader
                            title="Feedback"
                            size="sm"
                            actionsTray={
                              <InlineMenu>
                                <ContextMenu.Item
                                  key="configure-feedback"
                                  withinDropdownMenu
                                  href={
                                    getFileHref({
                                      slug,
                                      id: fileFromLog.id,
                                      page: "dashboard",
                                    }) + "?monitoring=true" // query param to open monitoring modal
                                  }
                                  IconRight={ArrowRightIcon}
                                >
                                  Configure Human Evaluators
                                </ContextMenu.Item>
                              </InlineMenu>
                            }
                          />
                          <DrawerFeedbackSection
                            // Key to force it to not hold on to state.
                            key={log.id}
                            datapoint={log}
                            evaluators={feedbackEvaluators}
                            onAddFeedback={onAddFeedback}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Tabs.Panel>

                {/* Performance */}
                {showPerformanceTab && (
                  <Tabs.Panel className="py-24">
                    <EvaluationDetails
                      log={log}
                      file={fileFromLog}
                      evaluatorLogs={log.evaluator_logs}
                      allowRunEvaluations={allowRunEvaluations}
                      onRunEvaluations={onRunEvaluations}
                      onFeedbackChange={onAddFeedback}
                    />
                    <div className="mt-64" />
                    <PageSectionHeader title="Feedback" size="sm" />
                    <DrawerFeedbackSection
                      datapoint={log}
                      evaluators={feedbackEvaluators}
                      onAddFeedback={onAddFeedback}
                    />
                    <div className="my-32 py-24">
                      <PageSectionHeader title="Feedback activity" size="sm" />
                      <FeedbackDetails logs={log.evaluator_logs ?? []} humanEvaluators={feedbackEvaluators} />
                    </div>
                  </Tabs.Panel>
                )}

                {/* File/version */}
                <Tabs.Panel className="py-24">
                  {/* TODO: Really need to expose and link to the Prompt but that needs some BE changes
                  and i'll save that for separate work. */}
                  <VersionDetails file={fileFromLog} />
                </Tabs.Panel>

                {/* API Request */}
                <Tabs.Panel className="py-24">
                  <div className="pb-48">
                    <PageSectionHeader title="Metadata" size="sm" />
                    <MetadataDetails log={log} />
                  </div>

                  {fileFromLog.type !== "flow" && (
                    <>
                      {log.provider_request && (
                        <div className="pb-48">
                          <PageSectionHeader title="Provider request" size="sm" />
                          <div className={classNames("text-11-14", PRIVACY_CLASSNAMES["high"])}>
                            <CodePreview language="json" wrapLongLines>
                              {JSON.stringify(log.provider_request, null, 2)}
                            </CodePreview>
                          </div>
                        </div>
                      )}
                      {log.provider_response && (
                        <div className="pb-48">
                          <PageSectionHeader title="Provider response" size="sm" />
                          <div className={classNames("text-11-14", PRIVACY_CLASSNAMES["high"])}>
                            <CodePreview language="json" wrapLongLines>
                              {JSON.stringify(log.provider_response, null, 2)}
                            </CodePreview>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Tabs.Panel>

                {/* JSON */}
                <Tabs.Panel className={classNames("py-24", PRIVACY_CLASSNAMES["high"])}>
                  <PageSectionHeader title="Log Object" size="sm" />
                  <DebugJsonViewer value={log} />
                </Tabs.Panel>
              </Tabs.Panels>
            </Tabs.Group>
          </div>
        )}
      </>
    </Drawer>
  );
};

export const LogDrawerFetcher = ({
  logId,
  open,
  setOpen,
  closeable,
  style,
  ...props
}: {
  logId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
} & Partial<LogDrawerProps>) => {
  const { log, mutate } = useLog(logId || null);
  // Sometimes the Log isn't ready yet and will return a 403. If the user then tries to open, the drawer, it might not respond,
  // as the swr fetch hadn't returned a log. So we'll try to fetch it again if the drawer is meant to be open.
  useEffect(() => {
    if (open && !log) {
      mutate();
    }
  }, [open, log, mutate]);

  useHotkeys("Escape", () => setOpen(false), { keydown: true });

  if (!log) return null;

  return (
    <LogDrawer
      open={open && !!log}
      onClose={() => setOpen(false)}
      onAddFeedback={mutate ? () => mutate() : () => {}}
      onRunEvaluations={() => {}}
      closeable={closeable}
      log={log}
      maskClosable
      style={style}
      {...props}
    />
  );
};
